import { JsonSchemaFormComponent } from '@ajsf/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  type OnDestroy,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';
import {
  FORM_FRAMEWORK,
  IPatientFormSchema,
  type IBaseFormLayoutElement,
  type ICustomFormData,
  type IFormSchema,
} from '@principle-theorem/principle-core/interfaces';
import {
  BehaviorSubject,
  ReplaySubject,
  Subject,
  combineLatest,
  type Observable,
} from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { PRINCIPLE_CUSTOM_FORM_WIDGETS } from './widgets/custom-form-widgets';

interface ICustomFormOptions {
  addSubmit: boolean | 'auto';
}

interface IJsonSchemaForm<T> {
  schema?: IFormSchema;
  layout?: IBaseFormLayoutElement[];
  data?: T;
  options?: ICustomFormOptions;
  framework?: string;
}

type CustomFormData<T extends object> =
  | ICustomFormData<T>
  | IPatientFormSchema<T>;

@Component({
  selector: 'pr-custom-form',
  templateUrl: './custom-form.component.html',
  styleUrls: ['./custom-form.component.scss'],
  providers: [{ provide: DateAdapter, useClass: NativeDateAdapter }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomFormComponent<T extends object> implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _schemaForm$ = new BehaviorSubject<
    JsonSchemaFormComponent | undefined
  >(undefined);
  private _form$ = new ReplaySubject<CustomFormData<T>>(1);
  private _hideSubmit$ = new BehaviorSubject<boolean>(false);
  isDisabled$ = new BehaviorSubject<boolean>(false);
  jsonSchemaForm$: Observable<IJsonSchemaForm<T>>;
  customWidgets = PRINCIPLE_CUSTOM_FORM_WIDGETS;
  @Output() submitted = new EventEmitter<object>();
  @Output() isValid = new EventEmitter<boolean>();
  @Output() formChanges = new EventEmitter<object>();

  @ViewChild(JsonSchemaFormComponent, { static: false })
  set schemaForm(schemaForm: JsonSchemaFormComponent) {
    this._schemaForm$.next(schemaForm);
  }

  @Input({ transform: coerceBooleanProperty })
  set disabled(readonly: boolean) {
    this.isDisabled$.next(readonly);
  }

  @Input()
  set form(form: CustomFormData<T>) {
    if (form) {
      this._form$.next(form);
    }
  }

  @Input({ transform: coerceBooleanProperty })
  set hideSubmit(hideSubmit: boolean) {
    this._hideSubmit$.next(hideSubmit);
  }

  constructor() {
    this.jsonSchemaForm$ = combineLatest([
      this._form$,
      this.isDisabled$,
      this._hideSubmit$,
    ]).pipe(
      map(
        ([form, isDisabled, hideSubmit]): IJsonSchemaForm<T> => ({
          ...form,
          framework: FORM_FRAMEWORK,
          options: { addSubmit: isDisabled || hideSubmit ? false : 'auto' },
        })
      )
    );
    combineLatest([this._schemaForm$, this.isDisabled$])
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(
        ([schemaForm, isDisabled]) =>
          schemaForm && this._setFormDisabled(schemaForm, isDisabled)
      );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  markAsTouched(): void {
    const formGroup = this._schemaForm$.value?.jsf.formGroup as
      | FormGroup
      | undefined;
    if (formGroup) {
      formGroup.markAllAsTouched();
    }
  }

  submit($event: object): void {
    this.markAsTouched();
    const wasDisabled = this.isDisabled$.value;
    if (wasDisabled) {
      return;
    }
    this.isDisabled$.next(true);
    this.submitted.emit($event);
    this.isDisabled$.next(wasDisabled);
  }

  private _setFormDisabled(
    form: JsonSchemaFormComponent,
    isDisabled: boolean
  ): void {
    if (!form || !form.jsf || !form.jsf.formGroup) {
      return;
    }
    form.jsf.setOptions({});
    const formGroup = form.jsf.formGroup as FormGroup;
    if (isDisabled) {
      formGroup.disable();
      return;
    }
    formGroup.enable();
  }
}
